import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {Check, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const MarketingPage = () => {
    const { service } = useParams();
    const { scrollYProgress } = useScroll();
    const scale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);

    const navigate = useNavigate();

    const contentData = {
        'facebook-ads': {
            title: 'Facebook Ads Marketing',
            description: 'Drive engagement and sales with targeted Facebook advertising campaigns',
            features: [
                'Advanced audience targeting and segmentation',
                'Custom ad creatives and A/B testing',
                'Performance tracking and optimization',
                'Campaign budget management',
                'Competitor analysis and market research',
                'Creative copywriting and design',
                'Custom landing pages',
                'Conversion tracking'
            ],
            benefits: [
                'Increase brand awareness',
                'Drive qualified traffic',
                'Boost conversions',
                'Improve ROI',
                'Build brand loyalty',
                'Reach mobile users',
                'Retarget potential customers',
                'Generate leads'
            ],
            testimonials: [
                {
                    name: 'John Smith',
                    company: 'Tech Solutions Inc',
                    content: 'Increased our ROI by 300% in just 3 months'
                },
                {
                    name: 'Sarah Johnson',
                    company: 'Fashion Boutique',
                    content: 'Doubled our social media engagement'
                }
            ],
            stats: {
                clients: '100+',
                campaigns: '1000+',
                roi: '300%',
                reach: '10M+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$499/mo',
                    features: ['Basic targeting', 'Monthly reports', 'Email support']
                },
                {
                    name: 'Professional',
                    price: '$999/mo',
                    features: ['Advanced targeting', 'Weekly reports', 'Priority support']
                },
                {
                    name: 'Enterprise',
                    price: 'Custom',
                    features: ['Custom solutions', 'Daily reports', '24/7 support']
                }
            ]
        },
        'email-marketing': {
            title: 'Email Marketing',
            description: 'Engage your audience with personalized and automated email campaigns',
            features: [
                'Automated email workflows',
                'Email list segmentation',
                'Responsive email templates',
                'Performance analytics',
                'Drip campaigns',
                'A/B testing for subject lines',
                'Customizable templates',
                'Lead nurturing'
            ],
            benefits: [
                'Boost engagement',
                'Nurture leads',
                'Increase conversions',
                'Strengthen customer loyalty',
                'Drive website traffic'
            ],
            testimonials: [
                {
                    name: 'Emily Davis',
                    company: 'Ecom Trends',
                    content: 'Email campaigns increased our customer retention by 40%'
                }
            ],
            stats: {
                clients: '300+',
                campaigns: '800+',
                roi: '250%',
                reach: '500K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$399/mo',
                    features: ['Automated campaigns', 'Monthly reports', 'Email support']
                },
                {
                    name: 'Professional',
                    price: '$799/mo',
                    features: ['Advanced automation', 'Weekly reports', 'Priority support']
                }
            ]
        },
        'seo-optimization': {
            title: 'SEO Optimization',
            description: 'Enhance your website’s visibility on search engines with our expert SEO services',
            features: [
                'On-page and off-page SEO',
                'Keyword research and analysis',
                'Technical SEO audits',
                'Backlink building',
                'Competitor analysis',
                'Local SEO optimization',
                'Content strategy integration',
                'Performance tracking'
            ],
            benefits: [
                'Improve search rankings',
                'Drive organic traffic',
                'Enhance brand credibility',
                'Boost local visibility'
            ],
            testimonials: [
                {
                    name: 'Mike Adams',
                    company: 'Startup Hub',
                    content: 'Achieved first-page rankings within 6 months'
                }
            ],
            stats: {
                clients: '200+',
                campaigns: '600+',
                roi: '400%',
                reach: '800K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$699/mo',
                    features: ['Basic SEO audit', 'Monthly keyword report']
                },
                {
                    name: 'Enterprise',
                    price: 'Custom',
                    features: ['Comprehensive SEO services', 'Dedicated account manager']
                }
            ]
        },
        'content-marketing': {
            title: 'Content Marketing',
            description: 'Craft compelling content that attracts and engages your audience',
            features: [
                'Blog writing',
                'Video production',
                'Infographics',
                'E-books and whitepapers',
                'Social media content',
                'SEO-optimized articles',
                'Content calendar creation',
                'Audience research'
            ],
            benefits: [
                'Increase engagement',
                'Drive organic traffic',
                'Establish thought leadership',
                'Boost social shares'
            ],
            testimonials: [
                {
                    name: 'Jessica Lee',
                    company: 'Health Boosters',
                    content: 'Our organic traffic tripled within a year'
                }
            ],
            stats: {
                clients: '150+',
                projects: '400+',
                roi: '350%',
                reach: '700K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$599/mo',
                    features: ['4 blog posts/month', 'Basic SEO', 'Monthly report']
                },
                {
                    name: 'Professional',
                    price: '$999/mo',
                    features: ['8 blog posts/month', 'Advanced SEO', 'Bi-weekly report']
                }
            ]
        },

        'google-ads': {
            title: 'Google Ads Management',
            description: 'Maximize your reach and drive conversions through expertly managed Google Ads campaigns',
            features: [
                'Keyword research and selection',
                'Ad copy optimization',
                'Bid management and budget control',
                'Landing page optimization',
                'Geo-targeting and audience segmentation',
                'Conversion tracking and reporting',
                'Competitor analysis',
                'A/B testing'
            ],
            benefits: [
                'Improve search visibility',
                'Boost website traffic',
                'Increase leads and sales',
                'Maximize ROI',
                'Outperform competitors',
                'Target local and global markets',
                'Instant traffic boost',
                'Flexible budget control'
            ],
            testimonials: [
                {
                    name: 'Michael Lee',
                    company: 'E-commerce World',
                    content: 'Our sales tripled in 2 months after starting Google Ads'
                },
                {
                    name: 'Emily Davis',
                    company: 'Real Estate Hub',
                    content: 'Achieved a 200% increase in qualified leads'
                }
            ],
            stats: {
                clients: '600+',
                campaigns: '1500+',
                roi: '250%',
                reach: '2M+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$599/mo',
                    features: ['Keyword research', 'Monthly performance reports', 'Email support']
                },
                {
                    name: 'Professional',
                    price: '$1199/mo',
                    features: ['Advanced bid management', 'Weekly reports', 'Priority support']
                },
                {
                    name: 'Enterprise',
                    price: 'Custom',
                    features: ['Custom solutions', 'Daily reports', '24/7 support']
                }
            ]
        },
        'social-media-strategy': {
            title: 'Social Media Strategy',
            description: 'Create a winning strategy to grow your brand across social media platforms',
            features: [
                'Platform-specific content strategies',
                'Audience analysis and engagement',
                'Content calendar planning',
                'Influencer collaboration',
                'Performance analytics',
                'Crisis management and response',
                'Paid promotions and campaigns',
                'Cross-platform management'
            ],
            benefits: [
                'Increase brand engagement',
                'Build brand authority',
                'Drive social traffic to your website',
                'Improve follower growth',
                'Strengthen customer relationships',
                'Generate leads through social channels',
                'Boost social conversions',
                'Enhance brand loyalty'
            ],
            testimonials: [
                {
                    name: 'Jessica Brown',
                    company: 'Beauty Co.',
                    content: 'We gained 50K followers in just 6 months'
                },
                {
                    name: 'Tom Wilson',
                    company: 'Gadget Geeks',
                    content: 'Our engagement rate increased by 150%'
                }
            ],
            stats: {
                clients: '400+',
                campaigns: '800+',
                roi: '200%',
                reach: '800K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$399/mo',
                    features: ['Content planning', 'Monthly performance reports', 'Email support']
                },
                {
                    name: 'Professional',
                    price: '$899/mo',
                    features: ['Advanced engagement', 'Weekly reports', 'Priority support']
                },
                {
                    name: 'Enterprise',
                    price: 'Custom',
                    features: ['Custom solutions', 'Daily reports', '24/7 support']
                }
            ]
        },
        'influencer-marketing': {
            title: 'Influencer Marketing',
            description: 'Leverage the power of influencers to grow your brand',
            features: [
                'Influencer identification and outreach',
                'Campaign strategy and execution',
                'Content collaboration and approvals',
                'Performance metrics and reporting',
                'Cross-platform campaigns',
                'Engagement tracking',
                'ROI measurement'
            ],
            benefits: [
                'Expand brand reach',
                'Build trust through authentic voices',
                'Engage niche audiences',
                'Boost product awareness',
                'Drive sales through recommendations'
            ],
            testimonials: [
                {
                    name: 'Emma Roberts',
                    company: 'Beauty Bliss',
                    content: 'Our product visibility skyrocketed with influencer partnerships'
                }
            ],
            stats: {
                clients: '300+',
                campaigns: '500+',
                roi: '250%',
                reach: '800K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$399/mo',
                    features: ['Micro-influencers', 'Monthly reports', 'Email support']
                },
                {
                    name: 'Professional',
                    price: '$899/mo',
                    features: ['Macro-influencers', 'Weekly reports', 'Priority support']
                }
            ]
        },
        'video-marketing': {
            title: 'Video Marketing',
            description: 'Engage your audience with compelling video content',
            features: [
                'Scriptwriting and storyboarding',
                'Video production and editing',
                'Multi-platform distribution',
                'Performance analysis',
                'SEO optimization for video',
                'Custom animations and effects'
            ],
            benefits: [
                'Boost audience engagement',
                'Improve conversion rates',
                'Strengthen brand messaging',
                'Reach wider audiences'
            ],
            testimonials: [
                {
                    name: 'Michael Lee',
                    company: 'Gadget World',
                    content: 'Our video ads doubled our customer base'
                }
            ],
            stats: {
                clients: '400+',
                campaigns: '600+',
                roi: '270%',
                reach: '900K+'
            },
            pricing: [
                {
                    name: 'Starter',
                    price: '$599/mo',
                    features: ['Short videos', 'Basic analytics', 'Email support']
                }
            ]
        },
        'mobile-marketing': {
            title: 'Mobile Marketing',
            description: 'Reach your audience directly through their mobile devices',
            features: [
                'SMS campaigns',
                'Push notifications',
                'Mobile app ads',
                'Geo-targeting',
                'Mobile-friendly landing pages'
            ],
            benefits: [
                'Increase mobile engagement',
                'Drive app downloads',
                'Enhance customer experience'
            ],
            testimonials: [
                {
                    name: 'Lucas White',
                    company: 'Retail Now',
                    content: 'Our mobile engagement increased by 50%'
                }
            ],
            pricing: [
                {
                    name: 'Starter',
                    price: '$599/mo',
                    features: ['Short videos', 'Basic analytics', 'Email support']
                }
            ],
            stats: {
                clients: '350+',
                campaigns: '550+',
                roi: '260%',
                reach: '850K+'
            }
        },
        'web-analytics': {
            title: 'Web Analytics',
            description: 'Analyze and optimize your website performance',
            features: [
                'Traffic analysis',
                'Conversion tracking',
                'User behavior insights',
                'Custom reports',
                'Heatmaps and session recordings'
            ],
            benefits: [
                'Understand user behavior',
                'Optimize conversion rates',
                'Enhance website performance'
            ],
            testimonials: [
                {
                    name: 'Anna Davis',
                    company: 'E-Shop Pro',
                    content: 'We optimized our site to improve conversions by 40%'
                }
            ],
            pricing: [
                {
                    name: 'Starter',
                    price: '$599/mo',
                    features: ['Short videos', 'Basic analytics', 'Email support']
                }
            ],
            stats: {
                clients: '450+',
                campaigns: '700+',
                roi: '280%',
                reach: '950K+'
            }
        }
    };


    const defaultContent = {
        title: 'Digital Marketing Excellence',
        description: 'Transform your online presence with our comprehensive digital marketing solutions',
        features: [
            'Custom strategy development',
            'Data-driven decisions',
            'Regular reporting',
            'Dedicated support',
            'Market research',
            'Brand development',
            'Content strategy',
            'Analytics setup'
        ],
        benefits: [
            'Increased visibility',
            'Higher conversion rates',
            'Better brand recognition',
            'Improved customer engagement',
            'Greater market reach',
            'Enhanced customer loyalty',
            'Measurable results',
            'Competitive advantage'
        ],
        testimonials: [
            {
                name: 'Alex Brown',
                company: 'Global Solutions',
                content: 'Outstanding results and professional service'
            },
            {
                name: 'Emily Davis',
                company: 'E-commerce Store',
                content: 'Transformed our online presence'
            },
            {
                name: 'David Miller',
                company: 'StartUp Inc',
                content: 'Exceptional ROI and great team'
            }
        ],
        stats: {
            clients: '1000+',
            campaigns: '5000+',
            roi: '200%',
            reach: '2M+'
        },
        pricing: [
            {
                name: 'Basic',
                price: '$299/mo',
                features: ['Essential services', 'Monthly reports', 'Email support']
            },
            {
                name: 'Growth',
                price: '$799/mo',
                features: ['Advanced services', 'Weekly reports', 'Priority support']
            },
            {
                name: 'Premium',
                price: 'Custom',
                features: ['Custom solutions', 'Daily reports', '24/7 support']
            }
        ]
    };

    const pageContent = contentData[service] || defaultContent;

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const slideIn = {
        hidden: { x: -60, opacity: 0 },
        visible: { x: 0, opacity: 1 }
    };

    return (
        <div className="min-h-screen  bg-white">

            {/* Hero Section */}
            <motion.section
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                className="bg-gradient-to-r from-black to-black py-24 px-4 relative overflow-hidden"
            >
                <motion.div
                    className="absolute inset-0 opacity-20"
                    style={{
                        background: "url('data:image/svg+xml,...')",
                        backgroundSize: 'cover'
                    }}
                    animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, 0]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
                <div className="container text-center mt-10 mx-auto max-w-6xl relative z-10">
                    <motion.h1
                        className="text-5xl md:text-7xl font-bold text-blue-600 mb-8"
                        initial={{ x: -100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        {pageContent.title}
                    </motion.h1>
                    <motion.p
                        className="text-2xl text-center text-white max-w-6xl mb-12"
                        initial={{ x: 100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {pageContent.description}
                    </motion.p>
                    <motion.button
                        className="bg-white text-black px-10 py-4 rounded-full text-xl font-semibold hover:bg-blue-600 transform hover:scale-105 transition-all"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/contact')}
                    >
                        Get Started Now
                    </motion.button>
                </div>
            </motion.section>

            {/* Benefits Section */}
            <motion.section
                className="py-20 px-4 bg-white"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Why Choose Our Service?
                    </motion.h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {pageContent.benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                className="bg-gray-50 p-6 rounded-xl hover:shadow-xl transition-shadow"
                                variants={fadeIn}
                                whileHover={{ y: -10 }}
                            >
                                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                                    <Check className="w-6 h-6 text-black" />
                                </div>
                                <h3 className="text-xl font-semibold mb-4">{benefit}</h3>
                                <p className="text-gray-600">
                                    Experience the difference with our proven strategies and expertise.
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Features Section with Timeline */}
            <motion.section
                className="py-20 px-4 bg-gray-50"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Our Comprehensive Approach
                    </motion.h2>
                    <div className="space-y-12">
                        {pageContent.features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="flex flex-col md:flex-row items-center gap-8"
                                variants={slideIn}
                            >
                                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-2xl font-bold">{index + 1}</span>
                                </div>
                                <div className="flex-1 bg-white p-8 rounded-xl shadow-lg">
                                    <h3 className="text-2xl font-semibold mb-4">{feature}</h3>
                                    <p className="text-gray-600">
                                        Leverage our expertise and proven methodologies to achieve exceptional results.
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Testimonials Section */}
            <motion.section
                className="py-20 px-4 bg-white text-blue-600"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Client Success Stories
                    </motion.h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {pageContent.testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                className="bg-gray-900 p-8 rounded-xl"
                                variants={fadeIn}
                                whileHover={{ scale: 1.05 }}
                            >
                                <div className="flex items-center gap-2 mb-6">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <p className="text-lg mb-6">{testimonial.content}</p>
                                <div>
                                    <p className="font-semibold">{testimonial.name}</p>
                                    <p className="text-white">{testimonial.company}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Pricing Section */}
            <motion.section
                className="py-20 px-4 bg-white"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Flexible Pricing Plans
                    </motion.h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {pageContent.pricing.map((plan, index) => (
                            <motion.div
                                key={index}
                                className="bg-gray-50 p-8 rounded-xl border-2 border-transparent hover:border-blue-600 transition-all"
                                variants={fadeIn}
                                whileHover={{ y: -20 }}
                            >
                                <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                                <p className="text-4xl font-bold mb-8">{plan.price}</p>
                                <ul className="space-y-4 mb-8">
                                    {plan.features.map((feature, fIndex) => (
                                        <li key={fIndex} className="flex items-center gap-2">
                                            <Check className="w-5 h-5 text-green-500" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button className="w-full bg-white text-blue-600 py-3 rounded-lg hover:bg-gray-900 transition-colors">
                                    Choose Plan
                                </button>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Stats Section */}
            <motion.section
                className="py-20 px-4 bg-white"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <motion.div variants={fadeIn}>
                            <h3 className="text-5xl text-indigo-600 font-bold mb-2">{pageContent.stats.clients}</h3>
                            <p className="text-xl font-semibold">Happy Clients</p>
                        </motion.div>
                        <motion.div variants={fadeIn}>
                            <h3 className="text-5xl  text-indigo-600 font-bold mb-2">{pageContent.stats.campaigns}</h3>
                            <p className="text-xl font-semibold">Campaigns</p>
                        </motion.div>
                        <motion.div variants={fadeIn}>
                            <h3 className="text-5xl  text-indigo-600 font-bold mb-2">{pageContent.stats.roi}</h3>
                            <p className="text-xl font-semibold">Average ROI</p>
                        </motion.div>
                        <motion.div variants={fadeIn}>
                            <h3 className="text-5xl  text-indigo-600 font-bold mb-2">{pageContent.stats.reach}</h3>
                            <p className="text-xl font-semibold">People Reached</p>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* CTA Section */}
            <motion.section
                className="py-20 px-4 bg-gray-600 text-blue-600"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-4xl text-center">
                    <motion.h2
                        className="text-4xl md:text-5xl font-bold mb-8"
                        variants={fadeIn}
                    >
                        Ready to Transform Your Business?
                    </motion.h2>



                    Ready to Transform Your Business?
                </div>
                <motion.p
                    className="text-xl text-center text-white mb-12"
                    variants={fadeIn}
                >
                    Join thousands of successful businesses that have already taken their digital presence to the next level
                </motion.p>
                <motion.div
                    className="flex flex-col md:flex-row gap-4 justify-center"
                    variants={staggerContainer}
                >
                    <motion.button
                        className="bg-blue-600 text-black px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-500"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/contact')}
                    >
                        Schedule a Free Consultation
                    </motion.button>
                    <motion.button
                        className="bg-white text-black px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/projects')}
                    >
                        View Case Studies
                    </motion.button>
                </motion.div>

            </motion.section>

            {/* Process Section */}
            <motion.section
                className="py-20 px-4 bg-white"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Our Proven Process
                    </motion.h2>
                    <div className="grid md:grid-cols-3 gap-12">
                        <motion.div
                            className="relative"
                            variants={fadeIn}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="absolute top-0 left-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center -mt-8 -ml-8">
                                <span className="text-2xl font-bold">1</span>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-xl h-full">
                                <h3 className="text-2xl font-bold mb-4">Strategy Development</h3>
                                <p className="text-gray-600">
                                    We begin by understanding your business goals and developing a customized strategy that aligns with your vision.
                                </p>
                            </div>
                        </motion.div>
                        <motion.div
                            className="relative"
                            variants={fadeIn}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="absolute top-0 left-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center -mt-8 -ml-8">
                                <span className="text-2xl font-bold">2</span>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-xl h-full">
                                <h3 className="text-2xl font-bold mb-4">Implementation</h3>
                                <p className="text-gray-600">
                                    Our expert team executes the strategy using industry best practices and cutting-edge tools.
                                </p>
                            </div>
                        </motion.div>
                        <motion.div
                            className="relative"
                            variants={fadeIn}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="absolute top-0 left-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center -mt-8 -ml-8">
                                <span className="text-2xl font-bold">3</span>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-xl h-full">
                                <h3 className="text-2xl font-bold mb-4">Optimization</h3>
                                <p className="text-gray-600">
                                    We continuously monitor, analyze, and optimize your campaigns to ensure maximum ROI.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* FAQ Section */}
            <motion.section
                className="py-20 px-4 bg-gray-50"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-4xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Frequently Asked Questions
                    </motion.h2>
                    <div className="space-y-6">
                        {[
                            {
                                q: "How long does it take to see results?",
                                a: "While results vary by campaign and industry, most clients see initial results within 30-60 days."
                            },
                            {
                                q: "What makes your service different?",
                                a: "Our data-driven approach, expert team, and proven track record set us apart from the competition."
                            },
                            {
                                q: "Do you offer custom solutions?",
                                a: "Yes, we tailor our services to meet your specific business needs and goals."
                            },
                            {
                                q: "What industries do you work with?",
                                a: "We work with businesses across various industries, from e-commerce to B2B services."
                            }
                        ].map((faq, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-6 rounded-xl shadow-sm"
                                variants={fadeIn}
                                whileHover={{ scale: 1.02 }}
                            >
                                <h3 className="text-xl font-semibold mb-3">{faq.q}</h3>
                                <p className="text-gray-600">{faq.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Contact Section */}
            {/* <motion.section
                className="py-20 px-4 bg-gradient-to-br from-blue-600 to-blue-500"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <div className="container mx-auto max-w-6xl">
                    <motion.h2
                        className="text-4xl font-bold text-center mb-16"
                        variants={fadeIn}
                    >
                        Let's Get Started
                    </motion.h2>
                    <div className="grid md:grid-cols-2 gap-12">
                        <motion.div
                            className="bg-white p-8 rounded-xl shadow-xl"
                            variants={slideIn}
                        >
                            <h3 className="text-2xl font-bold mb-6">Contact Us</h3>
                            <form className="space-y-6">
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Your Name"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-blue-600"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Your Email"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-blue-600"
                                    />
                                </div>
                                <div>
                                    <textarea
                                        placeholder="Your Message"
                                        rows="4"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-blue-600"
                                    ></textarea>
                                </div>
                                <button
                                    className="w-full bg-white text-white py-3 rounded-lg hover:bg-gray-900 transition-colors"
                                    type="submit"
                                >
                                    Send Message
                                </button>
                            </form>
                        </motion.div>
                        <motion.div
                            className="space-y-8"
                            variants={slideIn}
                        >
                            <div>
                                <h3 className="text-2xl font-bold mb-4">Our Office</h3>
                                <p className="text-lg">123 Marketing Street<br />Digital City, DC 12345</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold mb-4">Contact Info</h3>
                                <div className="space-y-4">
                                    <p className="flex items-center gap-3 text-lg">
                                        <Phone className="w-6 h-6" />
                                        (555) 123-4567
                                    </p>
                                    <p className="flex items-center gap-3 text-lg">
                                        <Mail className="w-6 h-6" />
                                        contact@example.com
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold mb-4">Business Hours</h3>
                                <p className="text-lg">Monday - Friday: 9:00 AM - 6:00 PM<br />Saturday: 10:00 AM - 4:00 PM</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section> */}
        </div>
    );
};

export default MarketingPage;